import { useCallback, useMemo } from 'react';

import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getItemTitleFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import type { DataModelAttribute, PersistedItem } from '@/types/DataModel';

/** Item layout of the Linked Articles table (D-043). */
const DAM_LINKED_ITEMS_LAYOUT = 'asset_linked_items';

interface DamLinkedItemColumns {
    columns: DataModelAttribute[];
    /** Product name from the layout's item title filter; `undefined` when the layout has none. */
    titleOf: ((item: PersistedItem) => string) | undefined;
}

/** Columns of `asset_linked_items` and the product name from its `item_title_filter`. */
function useDamLinkedItemColumns(): DamLinkedItemColumns {
    const { dataModel } = useDataModel();
    const { user } = useUser();
    const { organization } = useOrganization();
    const layout = dataModel?.layout(DAM_LINKED_ITEMS_LAYOUT);
    const filterName = layout?.layoutOptions?.itemTitleFilter;

    const columns = useMemo(() => (layout?.sections ?? [])
        .flatMap((section) => section.attributes ?? [])
        .filter((attribute) => !attribute.hidden)
        .map((attribute): DataModelAttribute => {
            const registered = dataModel?.attribute(attribute.name);
            return registered ? { ...registered, ...attribute } : attribute;
        }), [layout, dataModel]);

    const titleFilter = useMemo(() => {
        if (!filterName) {
            return undefined;
        }
        try {
            return getItemTitleFilter(filterName);
        } catch (error) {
            logger.error(`Item title filter ${filterName} is not registered`, error);
            return undefined;
        }
    }, [filterName]);
    const titleOf = useCallback((item: PersistedItem) => {
        try {
            return titleFilter?.(item, user, organization) || item.primaryKey__;
        } catch (error) {
            logger.error(`Item title filter ${filterName} failed`, error);
            return item.primaryKey__;
        }
    }, [titleFilter, user, organization, filterName]);

    return { columns, titleOf: titleFilter ? titleOf : undefined };
}

export { DAM_LINKED_ITEMS_LAYOUT, useDamLinkedItemColumns };
export type { DamLinkedItemColumns };
